import { faArrowLeft, faCalendarAlt, faCheck, faCheckCircle, faClipboardList, faDatabase, faEye, faFileExport, faMoneyCheck, faPlus, faRedo, faSpinner, faSync, faUpload, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Badge, Button, Card, Grid, Modal, Table, Tabs } from "@mantine/core";
import React, { useEffect, useState } from "react";
import ABAApi from "../../../data/api/abaApi";
import { IPayEvent, PayEventStatus } from "../../../types/aba";
import { IAssociation } from '../../../types/association/Association';
import { IPerson } from "../../../types/persons/person";
import BulkBankDetailsImport from "../settings/BulkBankDetailsImport";
import ABAExportSection from "./ABAExportSection";
import AppointmentPayEventCreator from "./AppointmentPayEventCreator";
import BankDetailsAuditLog from "./BankDetailsAuditLog";
import BSBUploadTab from "./BSBUploadTab";
import ShiftPayEventCreator from "./ShiftPayEventCreator";

interface AppointmentPaysTabProps {
  /** Selected association ID */
  association: IAssociation;
  /** Available associations */
  associations: Array<{ associationId: string; name: string }>;
  /** Callback when association changes */
  onAssociationChange: (associationId: string) => void;
  /** Current user for admin permissions */
  self: IPerson;
}

/**
 * Main tab for managing appointment payments and ABA exports
 * Combines pay event management with ABA export functionality
 */
const AppointmentPaysTab: React.FunctionComponent<AppointmentPaysTabProps> = ({
  association,
  onAssociationChange,
  self,
}) => {
  const [activeSubTab, setActiveSubTab] = useState("payevents");
  const [selectedPayEvent, setSelectedPayEvent] = useState<IPayEvent | null>(null);
  const [showPayEventDetails, setShowPayEventDetails] = useState(false);

  const associationId = association._id;

  const isAdmin = self?.admin === true;

  const subTabs = {
    payevents: "Pay Events",
    createevent: "Create from Appointments",
    createfromshifts: "Create from Shifts",
    bankimport: "Bank Details Import",
    ...(isAdmin && { bsbupload: "BSB Data Upload" }),
    ...(isAdmin && { audit: "Audit Log" }),
  };

  const handleViewPayEvent = (payEvent: IPayEvent) => {
    setSelectedPayEvent(payEvent);
    setShowPayEventDetails(true);
  };

  const handleClosePayEventDetails = () => {
    setSelectedPayEvent(null);
    setShowPayEventDetails(false);
  };

  const handlePayEventCreated = (payEvent?: IPayEvent) => {
    // If a pay event was created, show its details immediately
    if (payEvent) {
      setSelectedPayEvent(payEvent);
      setShowPayEventDetails(true);
    }
    // The PayEventsManager will automatically refresh
  };

  return (
    <div>

      {/* Sub-navigation */}
      <Tabs value={activeSubTab} onChange={setActiveSubTab} className="mb-4">
        <Tabs.List>
          {Object.entries(subTabs).map(([key, label]) => (
            <Tabs.Tab
              key={key}
              value={key}
              leftSection={
                <FontAwesomeIcon
                  icon={
                    key === "payevents" ? faMoneyCheck :
                      key === "createevent" ? faPlus :
                        key === "createfromshifts" ? faCalendarAlt :
                          key === "bankimport" ? faUpload :
                            key === "bsbupload" ? faDatabase :
                              faClipboardList
                  }
                />
              }
            >
              {label}
            </Tabs.Tab>
          ))}
        </Tabs.List>
        {/* Pay Events Management */}
        <Tabs.Panel value="payevents">
          <Card>
            <Card.Section withBorder p="md">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faMoneyCheck} className="me-2" />
                Pay Events Management
              </h5>
            </Card.Section>
            <div style={{ padding: "1rem" }}>
              <Alert color="blue" className="mb-4">
                <h6>Appointment Payments</h6>
                <p className="mb-0">
                  Manage pay events for appointment-based payments. Create pay events to group
                  appointments for processing, then export ABA files for bank processing.
                </p>
              </Alert>

              {/* Custom Pay Events Manager with pay event viewing */}
              <PayEventsManagerWithABA
                associationId={associationId}
                onViewPayEvent={handleViewPayEvent}
              />
            </div>
          </Card>
        </Tabs.Panel>

        {/* Create Pay Event from Appointments */}
        <Tabs.Panel value="createevent">
          <AppointmentPayEventCreator
            associationId={associationId}
            association={association}
            onPayEventCreated={handlePayEventCreated}
            isVisible={activeSubTab === "createevent"}
          />
        </Tabs.Panel>

        {/* Create Pay Event from Shift Sessions */}
        <Tabs.Panel value="createfromshifts">
          <ShiftPayEventCreator
            association={association}
            onPayEventCreated={handlePayEventCreated}
            isVisible={activeSubTab === "createfromshifts"}
          />
        </Tabs.Panel>

        {/* Bulk Bank Details Import */}
        <Tabs.Panel value="bankimport">
          <Card>
            <Card.Section withBorder p="md">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faUpload} className="me-2" />
                Bulk Bank Details Import
              </h5>
            </Card.Section>
            <div style={{ padding: "1rem" }}>
              <Alert color="blue" className="mb-4">
                <h6>Bank Details Management</h6>
                <p className="mb-0">
                  Import bank details for multiple users from a CSV file. This is useful for
                  setting up bank details in bulk before creating pay events. All data is
                  validated and processed securely.
                </p>
              </Alert>

              <BulkBankDetailsImport
                associationId={associationId}
                onImportComplete={(result) => {
                  // Could refresh any bank details stats or show notification
                  console.log('Import completed:', result);
                }}
              />
            </div>
          </Card>
        </Tabs.Panel>

        {/* BSB Data Upload - Admin Only */}
        {isAdmin && (
          <Tabs.Panel value="bsbupload">
            <BSBUploadTab />
          </Tabs.Panel>
        )}

        {/* Bank Details Audit Log - Admin Only */}
        {isAdmin && (
          <Tabs.Panel value="audit">
            <BankDetailsAuditLog associationId={associationId} />
          </Tabs.Panel>
        )}
      </Tabs>

      {/* Pay Event Details Modal */}
      {selectedPayEvent && (
        <PayEventDetailsModal
          payEvent={selectedPayEvent}
          isOpen={showPayEventDetails}
          onClose={handleClosePayEventDetails}
        />
      )}
    </div>
  );
};

/**
 * Modal for viewing pay event details and exporting ABA
 */
const PayEventDetailsModal: React.FunctionComponent<{
  payEvent: IPayEvent;
  isOpen: boolean;
  onClose: () => void;
}> = ({ payEvent, isOpen, onClose }) => {
  const [showABAExport, setShowABAExport] = useState(false);
  const [marking, setMarking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPayEvent, setCurrentPayEvent] = useState(payEvent);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [persons, setPersons] = useState<any[]>([]);
  const [showSummary, setShowSummary] = useState(false);

  // Update local state when prop changes
  useEffect(() => {
    setCurrentPayEvent(payEvent);
  }, [payEvent]);

  // Load detailed pay event data (including appointments) when modal opens
  useEffect(() => {
    if (isOpen && payEvent?._id && (!currentPayEvent?.appointments || currentPayEvent.appointments.length === 0)) {
      loadPayEventDetails();
    }
  }, [isOpen, payEvent?._id]);

  const loadPayEventDetails = async () => {
    if (!payEvent?._id) return;

    setLoadingDetails(true);
    setError(null);

    try {
      const response = await ABAApi.getPayEvent(payEvent._id);
      setCurrentPayEvent(response.data);
      setPersons(response.persons || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pay event details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleExportABA = () => {
    setShowABAExport(true);
  };

  const handleMarkAsPaid = async () => {
    setMarking(true);
    setError(null);

    try {
      const updatedPayEvent = await ABAApi.changePayEventState(
        currentPayEvent._id,
        'paid',
        'Manually marked as paid'
      );
      setCurrentPayEvent(updatedPayEvent);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to mark as paid");
    } finally {
      setMarking(false);
    }
  };

  // Group appointments by person for summary view
  const getPersonSummary = () => {
    if (!currentPayEvent.appointments || !persons) return [];

    const groupedByPerson = new Map();

    currentPayEvent.appointments.forEach(appointment => {
      const person = persons.find(p => p._id === appointment.personId);
      const personName = person ? `${person.firstName} ${person.lastName}` : 'Unknown Person';

      if (!groupedByPerson.has(appointment.personId)) {
        groupedByPerson.set(appointment.personId, {
          personId: appointment.personId,
          personName: personName,
          appointments: [],
          totalAmount: 0
        });
      }

      const personData = groupedByPerson.get(appointment.personId);
      personData.appointments.push(appointment);
      personData.totalAmount += appointment.amount || 0;
    });

    return Array.from(groupedByPerson.values());
  };

  if (!currentPayEvent) return <p>Internal error occurred</p>;

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      size="lg"
      title={
        <>
          <FontAwesomeIcon icon={faMoneyCheck} className="me-2" />
          Pay Event Details
        </>
      }
      styles={{
        body: {
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '70vh'
        },
        content: {
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* Paid Status Banner */}
      {currentPayEvent.state === PayEventStatus.Paid && (
        <Alert color="green" className="mb-0 rounded-0 border-0">
          <div className="d-flex align-items-center">
            <FontAwesomeIcon icon={faCheckCircle} className="me-3" size="lg" />
            <div>
              <strong>Payment Processed</strong>
              <div className="small">
                {(() => {
                  // Get who marked it as paid and when from state history
                  const paidStateChange = currentPayEvent.stateHistory?.find(change =>
                    change.to === 'paid' || change.to === 'Paid'
                  );
                  const paidBy = paidStateChange?.changedBy || 'Unknown';
                  const paidAt = paidStateChange?.changedAt || currentPayEvent.paidAt;

                  return (
                    <>
                      <FontAwesomeIcon icon={faUser} className="me-1" />
                      Marked as paid by <strong>{paidBy}</strong>
                      {paidAt && (
                        <span className="ms-2">
                          <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                          on {new Date(paidAt).toLocaleString()}
                        </span>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </Alert>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {!showABAExport ? (
          <>
            {/* Scrollable Content Area */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '1rem',
              paddingBottom: '0.5rem'
            }}>
              {/* Pay Event Summary */}
              <Card className="mb-3">
                <div style={{ padding: "1rem" }}>
                  <Grid>
                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <h5>{currentPayEvent.name || currentPayEvent.description}</h5>
                      {currentPayEvent.description && currentPayEvent.name && (
                        <p className="text-muted">{currentPayEvent.description}</p>
                      )}
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 6 }} className="text-end">
                      <Badge
                        color={currentPayEvent.state === "pending" ? "warning" :
                          currentPayEvent.state === "exported" ? "info" :
                            currentPayEvent.state === "paid" ? "success" : "secondary"}
                        className="fs-6 px-3 py-2"
                      >
                        {currentPayEvent.state ? currentPayEvent.state.charAt(0).toUpperCase() + currentPayEvent.state.slice(1) : "Draft"}
                      </Badge>
                    </Grid.Col>
                  </Grid>

                  <hr />

                  <Grid>
                    <Grid.Col span={{ base: 12, md: 3 }}>
                      <div className="text-center">
                        <div className="h4 mb-1">{currentPayEvent.summary?.appointmentCount || 0}</div>
                        <div className="text-muted small">Appointments</div>
                      </div>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 3 }}>
                      <div className="text-center">
                        <div className="h4 mb-1">${(currentPayEvent.summary?.totalAmount || 0).toFixed(2)}</div>
                        <div className="text-muted small">Total Amount</div>
                      </div>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 3 }}>
                      <div className="text-center">
                        <div className="h4 mb-1">{new Date(currentPayEvent.createdAt).toLocaleDateString()}</div>
                        <div className="text-muted small">Created</div>
                      </div>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 3 }}>
                      <div className="text-center">
                        <div className="h4 mb-1">{currentPayEvent.summary?.currency || 'AUD'}</div>
                        <div className="text-muted small">Currency</div>
                      </div>
                    </Grid.Col>
                  </Grid>
                </div>
              </Card>

              {/* Error Display */}
              {error && (
                <Alert color="red" className="mb-3">
                  <strong>Error:</strong> {error}
                </Alert>
              )}

              {/* Loading Details */}
              {loadingDetails && (
                <Card className="mb-3">
                  <div style={{ padding: "1rem" }} className="text-center py-4">
                    <div className="spinner-border text-primary mb-3" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <div>Loading appointment details...</div>
                  </div>
                </Card>
              )}

              {/* Appointment Summary */}
              {!loadingDetails && currentPayEvent.appointments && currentPayEvent.appointments.length > 0 && (
                <Card className="mb-3">
                  <Card.Section withBorder p="md">
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">Appointment Summary ({currentPayEvent.appointments.length})</h6>
                      <div>
                        <Button
                          color={showSummary ? "gray" : "blue"}

                          variant={!showSummary ? "outline" : undefined}
                          onClick={() => setShowSummary(false)}
                          className="me-2"
                        >
                          Detailed
                        </Button>
                        <Button
                          color={showSummary ? "blue" : "gray"}

                          variant={!showSummary ? "outline" : undefined}
                          onClick={() => setShowSummary(true)}
                        >
                          Summary
                        </Button>
                      </div>
                    </div>
                  </Card.Section>
                  <div style={{ padding: "1rem" }}>
                    {showSummary ? (
                      // Summary View - Group by Person
                      <Table striped>
                        <thead>
                          <tr>
                            <th>Person</th>
                            <th># Appointments</th>
                            <th>Total Paid</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getPersonSummary().map((personSummary) => (
                            <tr key={personSummary.personId}>
                              <td>
                                <strong>{personSummary.personName}</strong>
                              </td>
                              <td>
                                <Badge color="blue" className="text-capitalize">
                                  {personSummary.appointments.length} appointment{personSummary.appointments.length !== 1 ? 's' : ''}
                                </Badge>
                              </td>
                              <td>
                                <strong className="text-success">
                                  ${personSummary.totalAmount.toFixed(2)}
                                </strong>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="table-active">
                            <th colSpan={2}>Total</th>
                            <th>
                              <strong className="text-success">
                                ${(currentPayEvent.summary?.totalAmount || 0).toFixed(2)}
                              </strong>
                            </th>
                          </tr>
                        </tfoot>
                      </Table>
                    ) : (
                      // Detailed View - Individual Appointments
                      <Table striped>
                        <thead>
                          <tr>
                            <th>Person</th>
                            <th>Appointment Type</th>
                            <th>Paid</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentPayEvent.appointments.map((appointment, index) => {
                            const person = persons.find(p => p._id === appointment.personId);
                            const personName = person ? `${person.firstName} ${person.lastName}` : 'Unknown Person';

                            return (
                              <tr key={appointment.appointmentId || index}>
                                <td>
                                  <strong>{personName}</strong>
                                  <div className="small text-muted">
                                    {appointment.position}
                                  </div>
                                </td>
                                <td>
                                  <Badge
                                    color={appointment.type === 'game' ? 'primary' : 'success'}
                                    className="text-capitalize"
                                  >
                                    {appointment.type || 'Game'}
                                  </Badge>
                                  <div className="small text-muted mt-1">
                                    {appointment.start && new Date(appointment.start).toLocaleDateString()}
                                  </div>
                                </td>
                                <td>
                                  <strong className="text-success">
                                    ${(appointment.amount || 0).toFixed(2)}
                                  </strong>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="table-active">
                            <th colSpan={2}>Total</th>
                            <th>
                              <strong className="text-success">
                                ${(currentPayEvent.summary?.totalAmount || 0).toFixed(2)}
                              </strong>
                            </th>
                          </tr>
                        </tfoot>
                      </Table>
                    )}
                  </div>
                </Card>
              )}

            </div>

            {/* Fixed Action Buttons Footer */}
            <div style={{
              padding: '1rem',
              display: 'flex',
              justifyContent: 'center',
              gap: '0.75rem',
              flexShrink: 0
            }}>
              <Button
                color="blue"
                onClick={handleExportABA}
              >
                <FontAwesomeIcon icon={faFileExport} className="me-2" />
                Export ABA File
              </Button>

              {currentPayEvent.state !== 'processed' && currentPayEvent.state !== 'paid' && (
                <Button
                  color="green"
                  onClick={handleMarkAsPaid}
                  disabled={marking}
                >
                  {marking ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                      Marking as Paid...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCheck} className="me-2" />
                      Mark as Paid
                    </>
                  )}
                </Button>
              )}

              <Button
                color="gray" variant="outline"
                onClick={onClose}
              >
                Close
              </Button>
            </div>
          </>
        ) : (
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            padding: '1rem'
          }}>
            {/* ABA Export Section */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faFileExport} className="me-2" />
                Export ABA File
              </h5>
              <Button
                color="gray" variant="outline"

                onClick={() => setShowABAExport(false)}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Back to Summary
              </Button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }}>
              <ABAExportSection
                payEvent={currentPayEvent}
                onComplete={() => {
                  setShowABAExport(false);
                  onClose();
                }}
              />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};


const payEventStatusColors: { [key: string]: string } = {
  draft: "blue",
  pending: "yellow",
  exported: "blue",
  processed: "green",
  cancelled: "red",
  paid: "green"
};

/**
 * Wrapper component for PayEventsManager with pay event viewing
 */
const PayEventsManagerWithABA: React.FunctionComponent<{
  associationId: string;
  onViewPayEvent: (payEvent: IPayEvent) => void;
}> = ({ associationId, onViewPayEvent }) => {
  const [payEvents, setPayEvents] = useState<IPayEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPayEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      const events = await ABAApi.getPayEvents(associationId);
      setPayEvents(events);
    } catch (err) {
      console.error('PayEvents loading error:', err);
      if (err instanceof Error) {
        setError(err.message);
      } else if (typeof err === 'string') {
        setError(err);
      } else if (err && typeof err === 'object' && 'message' in err) {
        setError(err.message as string);
      } else {
        setError("Failed to load pay events");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPayEvents();
  }, [associationId]);

  if (loading) {
    return <div className="text-center py-4"><FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-muted" /></div>;
  }

  if (error) {
    return (
      <Alert color="red">
        <h6>Error Loading Pay Events</h6>
        <p className="mb-2">{error}</p>
        <Button color="red" variant="outline" onClick={loadPayEvents}>
          <FontAwesomeIcon icon={faRedo} className="me-2" />
          Retry
        </Button>
      </Alert>
    );
  }

  if (payEvents.length === 0) {
    return (
      <Alert color="blue">
        <h6>No Pay Events</h6>
        <p className="mb-0">
          No pay events found for this association. Create your first pay event using the
          "Create from Appointments" tab above.
        </p>
      </Alert>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <span className="text-muted">{payEvents.length} pay event{payEvents.length !== 1 ? 's' : ''}</span>
        <Button color="blue" variant="outline" onClick={loadPayEvents}>
          <FontAwesomeIcon icon={faSync} className="me-2" />
          Refresh
        </Button>
      </div>

      <Table striped highlightOnHover>
        <thead>
          <tr>
            <th>Name</th>
            <th>Created</th>
            <th>Appointments</th>
            <th>Total Amount</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {payEvents.map(payEvent => (
            <tr key={payEvent._id}>
              <td>
                <strong>{payEvent.name || payEvent.description}</strong>
                {payEvent.description && payEvent.name && (
                  <div className="small text-muted">{payEvent.description}</div>
                )}
              </td>
              <td>
                <div>{new Date(payEvent.createdAt).toLocaleDateString()}</div>
                <div className="small text-muted">
                  {new Date(payEvent.createdAt).toLocaleTimeString()}
                </div>
              </td>
              <td>
                <Badge color="gray">
                  {payEvent.summary?.appointmentCount || 0} appointments
                </Badge>
              </td>
              <td>
                <strong>${(payEvent.summary?.totalAmount || 0).toFixed(2)}</strong>
              </td>
              <td>
                <Badge
                  color={payEventStatusColors[payEvent?.state?.toLowerCase?.()] || "blue"}
                >
                  {payEvent.state ? payEvent?.state?.charAt(0).toUpperCase() + payEvent?.state?.slice?.(1) : "Draft"}
                </Badge>
              </td>
              <td>
                <Button
                  color="blue"
                  variant='outline'

                  onClick={() => onViewPayEvent(payEvent)}
                >
                  <FontAwesomeIcon icon={faEye} className="me-2" />
                  View & Export
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default AppointmentPaysTab;
